import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Mail, Plus, Send, Trash2, Pencil, Users, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Phase F.5 Batch 20 — Marketing & Email Blast admin page. */
export default function Marketing() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [audience, setAudience] = useState({ count: 0, sample: [] });
  const [testTo, setTestTo] = useState({ open: false, campaign: null, email: "" });

  const blank = {
    name: "", subject: "", body_html: "", body_text: "", from_name: "", reply_to: "",
    audience_clients: true, audience_employees: false, audience_tag: "",
  };

  const load = useCallback(async () => {
    try { setList((await axios.get(`${API}/marketing/campaigns`)).data?.campaigns || []); }
    catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const previewAudience = useCallback(async () => {
    if (!editing) return;
    try {
      const r = await axios.get(`${API}/marketing/audience/preview`, {
        params: {
          include_clients: editing.audience_clients,
          include_employees: editing.audience_employees,
          audience_tag: editing.audience_tag,
        },
      });
      setAudience(r.data);
    } catch (e) { /* */ }
  }, [editing]);
  useEffect(() => { if (editing) previewAudience(); }, [editing, previewAudience]);

  const save = async () => {
    try {
      const body = editing;
      const url = editing.id ? `${API}/marketing/campaigns/${editing.id}` : `${API}/marketing/campaigns`;
      const method = editing.id ? "put" : "post";
      await axios[method](url, body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try { await axios.delete(`${API}/marketing/campaigns/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const send = async (id) => {
    if (!window.confirm("Send this campaign to all audience emails right now?")) return;
    try {
      const r = await axios.post(`${API}/marketing/campaigns/${id}/send`);
      toast.success(`Sent ${r.data.sent}/${r.data.audience_size} — failed ${r.data.failed}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const testSend = async () => {
    try {
      await axios.post(`${API}/marketing/campaigns/${testTo.campaign.id}/test-send`, { to_email: testTo.email });
      toast.success("Test email queued");
      setTestTo({ open: false, campaign: null, email: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  return (
    <div className="space-y-4 p-4" data-testid="marketing-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="h-6 w-6" /> Marketing & Email Blast</h1>
          <p className="text-sm text-muted-foreground">Compose and send marketing emails to your client list.</p>
        </div>
        <Button onClick={() => setEditing({ ...blank })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mk-new-btn"><Plus className="h-4 w-4 mr-1" /> New campaign</Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Campaigns</CardTitle></CardHeader>
        <CardContent>
          {list.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="mk-empty">No campaigns yet.</p> :
            <div className="space-y-2" data-testid="mk-list">
              {list.map((c) => (
                <div key={c.id} className="p-3 border rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid={`mk-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      {c.name}
                      <Badge variant={c.status === "sent" ? "default" : c.status === "failed" ? "destructive" : "outline"}>{c.status}</Badge>
                      {c.sent_count > 0 && <span className="text-xs text-muted-foreground">sent {c.sent_count}</span>}
                      {c.failed_count > 0 && <span className="text-xs text-rose-600">failed {c.failed_count}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">Subject: {c.subject}</p>
                  </div>
                  <div className="flex gap-1">
                    {c.status !== "sent" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`mk-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => setTestTo({ open: true, campaign: c, email: "" })} data-testid={`mk-test-${c.id}`}>Test</Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => send(c.id)} data-testid={`mk-send-${c.id}`}><Send className="h-3.5 w-3.5 mr-1" /> Send</Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(c.id)} data-testid={`mk-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="mk-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit campaign" : "New campaign"}</DialogTitle>
            <DialogDescription>Audience: <Users className="inline h-3 w-3" /> {audience.count} recipient{audience.count === 1 ? "" : "s"}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><Label>Name (internal)</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="mk-name" /></div>
                <div className="col-span-2"><Label>Subject</Label><Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="mk-subject" /></div>
                <div><Label>From name</Label><Input value={editing.from_name || ""} onChange={(e) => setEditing({ ...editing, from_name: e.target.value })} data-testid="mk-from-name" /></div>
                <div><Label>Reply-to</Label><Input value={editing.reply_to || ""} onChange={(e) => setEditing({ ...editing, reply_to: e.target.value })} data-testid="mk-reply-to" /></div>
                <div className="col-span-2"><Label>HTML body</Label><Textarea rows={5} value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} data-testid="mk-body-html" /></div>
                <div className="col-span-2"><Label>Plain text fallback (optional)</Label><Textarea rows={2} value={editing.body_text || ""} onChange={(e) => setEditing({ ...editing, body_text: e.target.value })} data-testid="mk-body-text" /></div>
              </div>
              <div className="flex flex-wrap gap-4 pt-2 border-t">
                <label className="text-sm flex items-center gap-2"><Switch checked={!!editing.audience_clients} onCheckedChange={(v) => setEditing({ ...editing, audience_clients: v })} data-testid="mk-aud-clients" /> Clients</label>
                <label className="text-sm flex items-center gap-2"><Switch checked={!!editing.audience_employees} onCheckedChange={(v) => setEditing({ ...editing, audience_employees: v })} data-testid="mk-aud-employees" /> Employees</label>
                <div><Label className="text-xs">Tag filter (optional)</Label><Input value={editing.audience_tag || ""} placeholder="e.g. vip" onChange={(e) => setEditing({ ...editing, audience_tag: e.target.value })} className="h-8" data-testid="mk-aud-tag" /></div>
              </div>
              {audience.sample.length > 0 && (
                <div className="text-xs text-muted-foreground"><Eye className="inline h-3 w-3 mr-1" /> Sample: {audience.sample.slice(0, 5).join(", ")}{audience.sample.length > 5 ? "…" : ""}</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mk-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testTo.open} onOpenChange={(o) => !o && setTestTo({ open: false, campaign: null, email: "" })}>
        <DialogContent data-testid="mk-test-dialog">
          <DialogHeader>
            <DialogTitle>Send test email</DialogTitle>
            <DialogDescription>Sends a single email to the address you enter — does NOT record a recipient row.</DialogDescription>
          </DialogHeader>
          <Input type="email" placeholder="you@example.com" value={testTo.email} onChange={(e) => setTestTo({ ...testTo, email: e.target.value })} data-testid="mk-test-email" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestTo({ open: false, campaign: null, email: "" })}>Cancel</Button>
            <Button onClick={testSend} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mk-test-send-btn">Send test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
